/**
 * Tiny shared request cache — the v1 hover-preview pattern (a module-level
 * `Map<key, Promise>`) generalised for reuse across the API client.
 *
 * Two jobs:
 *   1. In-flight dedupe — concurrent callers for the same key share one promise
 *      (e.g. a hover-prefetch and the detail page that follows it, or several
 *      dashboard tiles issuing the identical metrics query).
 *   2. Short TTL reuse — a resolved value is served for `ttlMs` so an immediate
 *      navigation after a prefetch is instant. TTLs are deliberately short so
 *      data stays fresh; rejections are never cached.
 */

interface CacheEntry<T> {
  /** In-flight promise, present until it settles. */
  promise?: Promise<T>;
  /** Resolved value + expiry, present after a successful settle. */
  value?: T;
  expiresAt?: number;
}

const store = new Map<string, CacheEntry<unknown>>();

/** Default TTLs (ms) for the cached payload families. */
export const CACHE_TTL = {
  /** Entity detail (booking, rate card) — safe to reuse briefly on drill-in. */
  detail: 45_000,
  /** Metric queries — many identical tiles + slicer churn. */
  metric: 20_000,
  /** List first-pages warmed on nav hover. */
  list: 15_000,
  /** Reference maps (surcharge codes / aliases) — change rarely. */
  reference: 5 * 60_000,
  /** Home layout + metrics catalog — reused on dashboard revisit. */
  home: 60_000,
} as const;

/**
 * Return a cached/in-flight result for `key`, or invoke `fn` and cache it.
 * A rejected `fn` clears the entry so the next call retries cleanly.
 */
export function cached<T>(
  key: string,
  fn: () => Promise<T>,
  ttlMs: number,
): Promise<T> {
  const now = Date.now();
  const existing = store.get(key) as CacheEntry<T> | undefined;

  if (existing) {
    if (existing.promise) return existing.promise;
    if (
      existing.value !== undefined &&
      existing.expiresAt !== undefined &&
      existing.expiresAt > now
    ) {
      return Promise.resolve(existing.value);
    }
  }

  const entry: CacheEntry<T> = {};
  const promise = fn()
    .then((value) => {
      entry.value = value;
      entry.expiresAt = Date.now() + ttlMs;
      entry.promise = undefined;
      return value;
    })
    .catch((err) => {
      store.delete(key);
      throw err;
    });

  entry.promise = promise;
  store.set(key, entry as CacheEntry<unknown>);
  return promise;
}

/** Return a fresh cached value synchronously, if one exists. */
export function peekCached<T>(key: string): T | undefined {
  const now = Date.now();
  const existing = store.get(key) as CacheEntry<T> | undefined;
  if (
    existing?.value !== undefined &&
    existing.expiresAt !== undefined &&
    existing.expiresAt > now
  ) {
    return existing.value;
  }
  return undefined;
}

/** Drop a specific cache entry (e.g. after a mutation invalidates it). */
export function invalidateCached(key: string): void {
  store.delete(key);
}

/** Drop every entry whose key starts with `prefix`. */
export function invalidateCachedPrefix(prefix: string): void {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
}
